import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Building2, Check, Plus, Search, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { readableSupabaseError } from "@/lib/supabase/errors";
import { useTenantContext } from "@/features/auth/tenant-context";
import { createCustomer, getCustomer, listCustomers, updateCustomer, type Customer } from "@/features/customers/customer-service";

const inputClass = "h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100";
const selectClass = `${inputClass} appearance-none`;

function isOperator(role: string | undefined) {
  return role === "COMPANY_ADMIN" || role === "OPERATIONS";
}

function AccessNotice() {
  return (
    <Card>
      <CardContent className="py-16 text-center">
        <div className="mx-auto mb-4 grid size-12 place-items-center rounded-2xl bg-slate-100 text-slate-400"><Users className="size-6" /></div>
        <h1 className="text-xl font-semibold text-slate-950">Operational access required</h1>
        <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-500">Customer records are available to company administrators and operations users only.</p>
      </CardContent>
    </Card>
  );
}

export function CustomersPage() {
  const { activeTenant, activeMembership, isLoading: tenantLoading } = useTenantContext();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState<"all" | "active" | "inactive">("all");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!activeTenant || !isOperator(activeMembership?.role)) {
      setIsLoading(false);
      return;
    }
    let current = true;
    setIsLoading(true);
    setError(null);
    void listCustomers(activeTenant.id)
      .then((result) => { if (current) setCustomers(result); })
      .catch((loadError: unknown) => { if (current) setError(readableSupabaseError(loadError instanceof Error ? loadError : null, "Unable to load customers.")); })
      .finally(() => { if (current) setIsLoading(false); });
    return () => { current = false; };
  }, [activeMembership?.role, activeTenant]);

  const filteredCustomers = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return customers.filter((customer) => {
      const matchesSearch = !normalizedSearch || [customer.name, customer.code, customer.contact_person, customer.email, customer.phone]
        .filter(Boolean)
        .some((value) => value?.toLowerCase().includes(normalizedSearch));
      const matchesStatus = activeFilter === "all" || (activeFilter === "active" ? customer.is_active : !customer.is_active);
      return matchesSearch && matchesStatus;
    });
  }, [activeFilter, customers, search]);

  if (tenantLoading) return <div className="py-16 text-center text-sm text-slate-500">Loading company context…</div>;
  if (!isOperator(activeMembership?.role)) return <AccessNotice />;

  return (
    <div className="space-y-6">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-sky-600">Directory</p>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-950">Customers</h1>
          <p className="mt-2 text-sm text-slate-500">Manage the companies connected to your freight operations.</p>
        </div>
        <Link to="/customers/new" className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 text-sm font-semibold text-white shadow-sm hover:bg-slate-700">
          <Plus className="size-4" /> New customer
        </Link>
      </div>

      <Card>
        <CardContent className="flex flex-col gap-3 p-4 md:flex-row">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-3 size-4 text-slate-400" />
            <Input className="pl-9" placeholder="Search customers, codes, or contacts" value={search} onChange={(event) => setSearch(event.target.value)} />
          </div>
          <select aria-label="Customer status" className={`${selectClass} md:w-44`} value={activeFilter} onChange={(event) => setActiveFilter(event.target.value as typeof activeFilter)}>
            <option value="all">All customers</option>
            <option value="active">Active only</option>
            <option value="inactive">Inactive only</option>
          </select>
        </CardContent>
      </Card>

      {error && <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

      <Card className="overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Customer directory</CardTitle>
          <Badge>{filteredCustomers.length} shown</Badge>
        </CardHeader>
        {isLoading ? <div className="px-5 py-12 text-center text-sm text-slate-500">Loading customers…</div> : filteredCustomers.length === 0 ? (
          <div className="px-5 py-16 text-center">
            <Building2 className="mx-auto mb-3 size-7 text-slate-300" />
            <p className="text-sm font-semibold text-slate-700">{customers.length === 0 ? "No customers yet" : "No customers match your filters"}</p>
            <p className="mt-1 text-sm text-slate-500">{customers.length === 0 ? "Add your first customer to start creating shipment jobs." : "Try a different search or status filter."}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="border-y border-slate-100 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr><th className="px-5 py-3 font-semibold">Customer</th><th className="px-5 py-3 font-semibold">Code</th><th className="px-5 py-3 font-semibold">Contact</th><th className="px-5 py-3 font-semibold">Email</th><th className="px-5 py-3 font-semibold">Phone</th><th className="px-5 py-3 font-semibold">Status</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredCustomers.map((customer) => (
                  <tr key={customer.id} className="transition-colors hover:bg-slate-50">
                    <td className="px-5 py-4"><Link to={`/customers/${customer.id}`} className="font-semibold text-slate-900 hover:text-sky-700">{customer.name}</Link></td>
                    <td className="px-5 py-4 text-slate-500">{customer.code || "—"}</td>
                    <td className="px-5 py-4 text-slate-600">{customer.contact_person || "—"}</td>
                    <td className="px-5 py-4 text-slate-600">{customer.email || "—"}</td>
                    <td className="px-5 py-4 text-slate-600">{customer.phone || "—"}</td>
                    <td className="px-5 py-4"><Badge className={customer.is_active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}>{customer.is_active ? "Active" : "Inactive"}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

interface CustomerFormState {
  name: string;
  code: string;
  contact_person: string;
  email: string;
  phone: string;
  address: string;
  is_active: boolean;
}

const emptyForm: CustomerFormState = { name: "", code: "", contact_person: "", email: "", phone: "", address: "", is_active: true };

export function CustomerFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id) && id !== "new";
  const navigate = useNavigate();
  const { activeTenant, activeMembership, isLoading: tenantLoading } = useTenantContext();
  const [form, setForm] = useState<CustomerFormState>(emptyForm);
  const [isLoading, setIsLoading] = useState(isEditing);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!activeTenant || !isEditing || !id) { setIsLoading(false); return; }
    let current = true;
    void getCustomer(activeTenant.id, id)
      .then((customer) => {
        if (!current) return;
        if (!customer) { setError("Customer not found."); return; }
        setForm({ name: customer.name, code: customer.code ?? "", contact_person: customer.contact_person ?? "", email: customer.email ?? "", phone: customer.phone ?? "", address: customer.address ?? "", is_active: customer.is_active });
      })
      .catch((loadError: unknown) => { if (current) setError(readableSupabaseError(loadError instanceof Error ? loadError : null, "Unable to load customer.")); })
      .finally(() => { if (current) setIsLoading(false); });
    return () => { current = false; };
  }, [activeTenant, id, isEditing]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!activeTenant) return;
    if (form.name.trim().length < 2) { setError("Enter a customer name."); return; }
    setError(null); setSuccess(null); setIsSaving(true);
    const input = { ...form, name: form.name.trim(), code: form.code.trim() || null, contact_person: form.contact_person.trim() || null, email: form.email.trim() || null, phone: form.phone.trim() || null, address: form.address.trim() || null };
    try {
      if (isEditing && id) {
        await updateCustomer(activeTenant.id, id, input);
        setSuccess("Customer updated.");
      } else {
        const created = await createCustomer(activeTenant.id, input);
        navigate(`/customers/${created.id}`, { replace: true });
        return;
      }
    } catch (saveError: unknown) {
      setError(readableSupabaseError(saveError instanceof Error ? saveError : null, "Unable to save customer."));
    } finally { setIsSaving(false); }
  }

  if (tenantLoading || isLoading) return <div className="py-16 text-center text-sm text-slate-500">Loading customer…</div>;
  if (!isOperator(activeMembership?.role)) return <AccessNotice />;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Link to="/customers" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-900"><ArrowLeft className="size-4" /> Back to customers</Link>
      <div><p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-sky-600">Customer directory</p><h1 className="text-3xl font-semibold tracking-tight text-slate-950">{isEditing ? "Edit customer" : "New customer"}</h1><p className="mt-2 text-sm text-slate-500">Keep the customer record ready for shipment operations.</p></div>
      <Card>
        <CardContent className="p-6">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="sm:col-span-2"><Label htmlFor="customer-name">Customer name *</Label><Input id="customer-name" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required /></div>
              <div><Label htmlFor="customer-code">Customer code</Label><Input id="customer-code" value={form.code} onChange={(event) => setForm({ ...form, code: event.target.value })} placeholder="e.g. ACME-001" /></div>
              <div><Label htmlFor="customer-contact">Contact person</Label><Input id="customer-contact" value={form.contact_person} onChange={(event) => setForm({ ...form, contact_person: event.target.value })} /></div>
              <div><Label htmlFor="customer-email">Email</Label><Input id="customer-email" type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} /></div>
              <div><Label htmlFor="customer-phone">Phone</Label><Input id="customer-phone" value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} /></div>
              <div className="sm:col-span-2"><Label htmlFor="customer-address">Address</Label><textarea id="customer-address" className={`${inputClass} min-h-24 py-3`} value={form.address} onChange={(event) => setForm({ ...form, address: event.target.value })} /></div>
              <label className="flex items-center gap-3 text-sm font-medium text-slate-700 sm:col-span-2"><input type="checkbox" checked={form.is_active} onChange={(event) => setForm({ ...form, is_active: event.target.checked })} className="size-4 rounded border-slate-300 text-slate-900" /> Active customer</label>
            </div>
            {error && <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-700">{error}</div>}
            {success && <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-700"><Check className="size-4" />{success}</div>}
            <div className="flex justify-end gap-3 border-t border-slate-100 pt-5"><Link to="/customers" className="inline-flex h-10 items-center rounded-lg px-4 text-sm font-semibold text-slate-600 hover:bg-slate-100">Cancel</Link><Button type="submit" disabled={isSaving}>{isSaving ? "Saving…" : isEditing ? "Save changes" : "Create customer"}</Button></div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
